import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const toggleRole = async () => {
    // Allow toggle for master users OR dual-role users
    const isDualRoleUser = user?.is_dual_role || (user?.roles && user?.roles.length > 1);
    if (user?.role !== 'master' && !isDualRoleUser) return;
    
    try {
      const response = await axios.post(`${API_URL}/admin/toggle-role`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { token: newToken, active_role } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(prev => ({ ...prev, active_role }));
      return active_role;
    } catch (error) {
      console.error('Error toggling role:', error);
      throw error;
    }
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  // Get effective role (for master or dual-role users, use active_role)
  const getEffectiveRole = () => {
    if (user?.role === 'master') {
      return user.active_role || 'field_qc_engineer';
    }
    // For dual-role users
    if (user?.is_dual_role || (user?.roles && user?.roles.length > 1)) {
      return user.active_role || user?.roles?.[0] || user?.role;
    }
    return user?.role;
  };

  const isMaster = () => user?.role === 'master' || user?.is_master;
  
  // Check if user can toggle between roles (master or dual-role)
  const canToggleRole = () => {
    return user?.role === 'master' || user?.is_master || user?.is_dual_role || (user?.roles && user?.roles.length > 1);
  };

  const isDualRole = () => user?.is_dual_role || (user?.roles && user?.roles.length > 1);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      getAuthHeaders, 
      toggleRole,
      getEffectiveRole,
      isMaster,
      canToggleRole,
      isDualRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
